import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { downloadFile } from "@/lib/api";
import {
  Loader2, RefreshCw, FileDown, FileText, ChevronDown, Circle, Clock,
  Timer, CalendarDays, CalendarRange, History, Users, X as XIcon,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/New_York", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    }) + " ET";
  } catch { return "—"; }
};

const hrs = (h) => `${(h ?? 0).toFixed(1)}h`;

function relative(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function HoursChart({ data, height = 150, color = "#2563eb", testid }) {
  const hasData = (data || []).some((d) => d.hours > 0);
  return (
    <div data-testid={testid} style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={34} allowDecimals={false} />
          <Tooltip
            cursor={{ fill: "rgba(37,99,235,0.06)" }}
            contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
            formatter={(v) => [`${v}h`, "Hours"]}
          />
          <Bar dataKey="hours" fill={hasData ? color : "#e2e8f0"} radius={[4, 4, 0, 0]} maxBarSize={26} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function OpsActivityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [applied, setApplied] = useState({ start: "", end: "" });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (applied.start && applied.end) { params.start = applied.start; params.end = applied.end; }
      const r = await api.get("/admin/ops-activity", { params });
      setData(r.data);
    } catch { toast.error("Couldn't load team activity."); }
    finally { setLoading(false); }
  }, [applied]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh live status every 30s
    return () => clearInterval(t);
  }, [load]);

  const exportUrl = (fmt) => {
    const q = new URLSearchParams({ fmt });
    if (applied.start && applied.end) { q.set("start", applied.start); q.set("end", applied.end); }
    return `/admin/ops-activity/export?${q.toString()}`;
  };

  const applyRange = () => {
    if (!range.start || !range.end) { toast.error("Pick both a start and end date."); return; }
    setApplied({ start: range.start, end: range.end });
  };
  const clearRange = () => { setRange({ start: "", end: "" }); setApplied({ start: "", end: "" }); };

  if (loading && !data)
    return <p className="p-10 text-center text-slate-400 flex items-center justify-center gap-2" data-testid="ops-activity-loading"><Loader2 size={16} className="animate-spin" /> Loading team activity…</p>;

  const managers = data?.managers || [];
  const onlineCount = data?.online_count ?? managers.filter((m) => m.online).length;
  const totals = data?.totals || { today: 0, week: 0, month: 0, all: 0 };
  const teamDaily = data?.team_daily || [];
  const rng = data?.range || { active: false };

  const summary = [
    { icon: Circle, label: "Online now", value: onlineCount, suffix: "", tid: "online", accent: "text-emerald-600" },
    { icon: Timer, label: "Team hours today", value: hrs(totals.today), tid: "today" },
    { icon: CalendarDays, label: "Team hours this week", value: hrs(totals.week), tid: "week" },
    { icon: CalendarRange, label: "Team hours this month", value: hrs(totals.month), tid: "month" },
  ];

  return (
    <div data-testid="ops-activity-panel" className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600" data-testid="ops-online-count">
            <Circle size={9} className="fill-emerald-500 text-emerald-500" /> {onlineCount} online
          </span>
          <span className="text-slate-300">·</span>
          <span>{managers.length} operations manager{managers.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-2 py-1" data-testid="ops-range-picker">
            <input type="date" value={range.start} max={range.end || undefined} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} data-testid="ops-range-start" className="text-sm text-slate-700 outline-none bg-transparent" />
            <span className="text-slate-300">→</span>
            <input type="date" value={range.end} min={range.start || undefined} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} data-testid="ops-range-end" className="text-sm text-slate-700 outline-none bg-transparent" />
            <button onClick={applyRange} data-testid="ops-range-apply" className="text-xs font-bold text-white bg-blue-600 rounded-full px-3 py-1 hover:bg-blue-700">Apply</button>
          </div>
          <button onClick={load} data-testid="ops-refresh" title="Refresh" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-full px-3.5 py-1.5 hover:bg-slate-50">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => downloadFile(exportUrl("csv")).catch(() => toast.error("Export failed"))} data-testid="ops-export-csv" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-full px-3.5 py-1.5 hover:bg-slate-50">
            <FileDown size={14} /> CSV
          </button>
          <button onClick={() => downloadFile(exportUrl("pdf")).catch(() => toast.error("Export failed"))} data-testid="ops-export-pdf" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 rounded-full px-3.5 py-1.5 hover:bg-blue-700">
            <FileText size={14} /> PDF
          </button>
        </div>
      </div>

      {/* Selected pay-period banner */}
      {rng.active && (
        <div data-testid="ops-range-banner" className="flex items-center justify-between gap-4 flex-wrap rounded-2xl border-2 border-blue-200 bg-blue-50/60 px-5 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-blue-500 font-bold">Selected period</p>
            <p className="font-heading text-lg font-bold text-slate-900">{rng.start} → {rng.end}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-heading text-2xl font-bold text-blue-700" data-testid="ops-range-total">{hrs(rng.total)}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Total team hours</p>
            </div>
            <button onClick={clearRange} data-testid="ops-range-clear" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 hover:bg-slate-50">
              <XIcon size={14} /> Clear
            </button>
          </div>
        </div>
      )}

      {/* Team summary (hidden when a custom period is active) */}
      {!rng.active && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ops-team-summary">
          {summary.map((s) => (
            <div key={s.tid} data-testid={`ops-summary-${s.tid}`} className="bg-white rounded-2xl border border-slate-200 p-5">
              <span className={`grid place-items-center w-9 h-9 rounded-xl bg-slate-50 ${s.accent || "text-blue-600"}`}><s.icon size={16} className={s.tid === "online" ? "fill-emerald-500 text-emerald-500" : ""} /></span>
              <p className="font-heading text-2xl font-bold text-slate-900 mt-3">{s.value}</p>
              <p className="label-caps text-slate-400 mt-1 text-[10px]">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Team daily chart */}
      {managers.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="ops-team-chart-card">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} className="text-blue-600" />
            <h3 className="font-semibold text-slate-900 text-sm">{rng.active ? `Team hours — ${rng.start} to ${rng.end}` : "Team hours — last 14 days"}</h3>
          </div>
          <HoursChart data={teamDaily} height={200} testid="ops-team-chart" />
        </div>
      )}

      {managers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400" data-testid="ops-activity-empty">
          No operations managers yet. Promote a user to "Operations Manager" in Users &amp; Login to start tracking work hours.
        </div>
      ) : (
        <div className="space-y-4">
          {managers.map((m) => (
            <div key={m.user_id} data-testid={`ops-card-${m.user_id}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="relative grid place-items-center w-11 h-11 rounded-full bg-violet-100 text-violet-700 font-heading font-bold shrink-0">
                    {(m.name || m.email || "?").charAt(0).toUpperCase()}
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${m.online ? "bg-emerald-500" : "bg-slate-300"}`} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{m.name || m.email}</p>
                    <p className="text-xs text-slate-500 truncate">{m.email}</p>
                  </div>
                </div>
                <div className="shrink-0">
                  {m.online ? (
                    <span data-testid={`ops-status-${m.user_id}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1.5">
                      <Circle size={8} className="fill-emerald-500 text-emerald-500 animate-pulse" /> Online now
                    </span>
                  ) : (
                    <span data-testid={`ops-status-${m.user_id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-3 py-1.5">
                      <Clock size={12} /> Last seen {relative(m.last_seen) || "—"}
                    </span>
                  )}
                </div>
              </div>

              {/* Hours */}
              {rng.active ? (
                <div className="border-t border-slate-100 px-5 py-4 flex items-center justify-between" data-testid={`ops-range-hours-${m.user_id}`}>
                  <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold"><CalendarRange size={12} /> Hours in period · {m.session_count} session{m.session_count === 1 ? "" : "s"}</span>
                  <p className="font-heading text-xl font-bold text-blue-700">{hrs(m.range_hours)}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 border-t border-slate-100 divide-x divide-slate-100">
                  {[
                    { icon: Timer, label: "Today", value: m.hours.today, tid: "today" },
                    { icon: CalendarDays, label: "This week", value: m.hours.week, tid: "week" },
                    { icon: CalendarRange, label: "This month", value: m.hours.month, tid: "month" },
                    { icon: History, label: "All-time", value: m.hours.all, tid: "all" },
                  ].map((c) => (
                    <div key={c.tid} data-testid={`ops-hours-${c.tid}-${m.user_id}`} className="px-5 py-4">
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold"><c.icon size={12} /> {c.label}</span>
                      <p className="font-heading text-xl font-bold text-slate-900 mt-1">{hrs(c.value)}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Per-manager chart */}
              <div className="border-t border-slate-100 px-5 py-4" data-testid={`ops-chart-wrap-${m.user_id}`}>
                <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">{rng.active ? "Hours in selected period" : "Hours — last 14 days"}</p>
                <HoursChart data={m.daily} height={130} testid={`ops-chart-${m.user_id}`} />
              </div>

              {/* Session history */}
              <button onClick={() => setOpen(open === m.user_id ? null : m.user_id)} data-testid={`ops-sessions-toggle-${m.user_id}`}
                className="w-full flex items-center justify-between px-5 py-3 border-t border-slate-100 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <span>Session history ({m.session_count})</span>
                <ChevronDown size={16} className={`transition-transform ${open === m.user_id ? "rotate-180" : ""}`} />
              </button>
              {open === m.user_id && (
                <div className="border-t border-slate-100 max-h-80 overflow-auto" data-testid={`ops-sessions-${m.user_id}`}>
                  {m.sessions.length === 0 ? (
                    <p className="px-5 py-6 text-center text-slate-400 text-sm">No sessions recorded yet.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide sticky top-0">
                        <tr>
                          <th className="text-left px-5 py-2.5 font-semibold">Clock in</th>
                          <th className="text-left px-5 py-2.5 font-semibold">Clock out</th>
                          <th className="text-right px-5 py-2.5 font-semibold">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {m.sessions.map((s, i) => (
                          <tr key={i} className="border-t border-slate-50">
                            <td className="px-5 py-2.5 text-slate-700">{fmtDate(s.login_at)}</td>
                            <td className="px-5 py-2.5 text-slate-700">
                              {s.active ? <span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold"><Circle size={7} className="fill-emerald-500 text-emerald-500" /> Active</span> : fmtDate(s.ended_at)}
                            </td>
                            <td className="px-5 py-2.5 text-right font-semibold text-slate-900">{hrs(s.duration_hours)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
